/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/experiment-workflow/started-at
 * @desc Column Definition for experiment workflow started_at
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import { setValue } from 'labstep-web/core/DataGrid/utils';
import { FormShowEditDateToggleComponent } from 'labstep-web/core/Form/ShowEdit/Date/ToggleComponent';
import type { IFormShowEditDateToggleComponentProps } from 'labstep-web/core/Form/ShowEdit/Date/ToggleComponent/types';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import { PostFilterFieldTypes } from 'labstep-web/grid/services/grid-filter.service';
import type { Entity } from 'labstep-web/models/entity.model';
import {
  formatDateOnly,
  humanReadableDate,
} from 'labstep-web/services/date.service';
import rules from 'labstep-web/services/validation/rules';

export const colDefExperimentWorkflowStartedAt: NewIndexColDef = {
  colId: 'started_at',
  headerName: 'Date Started',
  headerComponentParams: {
    icon: ICONS.metadata.type.datetime,
    postFilterSettings: {
      fieldType: PostFilterFieldTypes.date,
      field: 'started_at',
      nullableField: false,
      sort: 'started_at',
    },
  },
  editable: (params: EditableCallbackParams) => {
    if (params.data.entity_state && !params.data.started_at) {
      return false;
    }
    return GridNewIndexService.getEditable(params);
  },
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
  valueClearer: (params: CellEditorParams<Entity>) => {
    if (!params.node) {
      return null;
    }
    setValue(
      'started_at',
      null,
      {
        entityName: params.data.entityName,
        id: params.data.id,
      },
      rules.experiment_workflow.started_at,
      GridColumnDefinitionService.getOptions(params),
    );
  },
  valueGetter: (params: ValueGetterParams) => params.data.started_at,
  valueGetterExport: (params: ValueGetterParams) =>
    params.node?.data?.started_at
      ? String(params.node?.data?.started_at)
      : '',
  valueSetter: (params: ValueSetterParams) =>
    setValue(
      'started_at',
      params.newValue,
      {
        entityName: params.data.entityName,
        id: params.data.id,
      },
      rules.experiment_workflow.started_at,
      GridColumnDefinitionService.getOptions(params),
    ),
  cellRenderer: (params: CellRendererParams<Entity>) => {
    if (!params.data) {
      return '';
    }

    if (!params.data.started_at && !params.data.entity_state) {
      return (
        <DataGridPlaceholder
          params={params}
          editable={GridNewIndexService.getEditable(params)}
          placeholder="Enter"
        />
      );
    }

    if (!params.data.started_at) {
      return null;
    }

    return <>{humanReadableDate(params.data.started_at)}</>;
  },
  cellEditorPopup: true,
  cellEditorPopupPosition: 'under',
  cellEditor: (params: CellRendererParams<Entity>) => {
    if (!params.node) {
      return null;
    }

    const onCloseFn: IFormShowEditDateToggleComponentProps['onClose'] =
      (date): void => {
        if (!date || date.length !== 1) {
          return;
        }

        const newValue = formatDateOnly(date[0]);
        setValue(
          'started_at',
          newValue,
          {
            entityName: params.data.entityName,
            id: params.data.id,
          },
          rules.experiment_workflow.started_at,
          GridColumnDefinitionService.getOptions(params),
        );
      };

    return (
      <FormShowEditDateToggleComponent
        canEdit
        dateTimePickerOnly
        viewComponent={<TextPlaceholder></TextPlaceholder>}
        defaultDate={params.node.data?.started_at || null}
        onClose={onCloseFn}
        dateOnly
      />
    );
  },
};
